import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import ScheduleExceptions from '../components/ScheduleExceptions';
import '../styles/SettingsPage.css';

const ALL_DAYS = [
  { en: 'Sunday', ar: 'الأحد' },
  { en: 'Monday', ar: 'الإثنين' },
  { en: 'Tuesday', ar: 'الثلاثاء' },
  { en: 'Wednesday', ar: 'الأربعاء' },
  { en: 'Thursday', ar: 'الخميس' },
  { en: 'Friday', ar: 'الجمعة' },
  { en: 'Saturday', ar: 'السبت' },
];

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workingDays, setWorkingDays] = useState([]);
  const [startHour, setStartHour] = useState('09:00');
  const [endHour, setEndHour] = useState('18:00');
  const [globalBreaks, setGlobalBreaks] = useState([]);    // [{start:'12:00', end:'14:00'}, ...]
  const [dayHours, setDayHours] = useState({});             // {Monday: {start:'09:00', end:'21:00'}}
  const [perDayOffHours, setPerDayOffHours] = useState({}); // {Monday: ['12:00-15:00']}
  const [existingVacationDays, setExistingVacationDays] = useState([]);
  const [autoDeleteOldReservations, setAutoDeleteOldReservations] = useState(true);
  const [autoDeleteSaving, setAutoDeleteSaving] = useState(false);

  const parseBreaks = (arr) =>
    (arr || []).map((s) => {
      const parts = s.split('-');
      return { start: parts[0]?.trim() || '12:00', end: parts[1]?.trim() || '13:00' };
    });

  const serializeBreaks = (breaks) =>
    breaks
      .filter((b) => b.start && b.end && b.start < b.end)
      .map((b) => `${b.start}-${b.end}`);

  useEffect(() => {
    (async () => {
      try {
        const [calRes, autoDelRes] = await Promise.all([
          api.get('/backoffice/calendar-settings'),
          api.get('/backoffice/auto-delete-old-reservations'),
        ]);
        if (calRes.data.success) {
          const s = calRes.data.settings;
          setWorkingDays(s.working_days || []);
          setStartHour(s.start_hour || '09:00');
          setEndHour(s.end_hour || '18:00');
          setGlobalBreaks(parseBreaks(s.global_breaks || []));
          setDayHours(s.day_hours || {});
          setPerDayOffHours(s.off_hours || {});
          setExistingVacationDays(s.vacation_days || []);
        }
        if (autoDelRes.data.success) {
          setAutoDeleteOldReservations(autoDelRes.data.auto_delete_old_reservations !== false);
        }
      } catch {
        // no settings yet — defaults are fine
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line
  }, []);

  const toggleAutoDeleteOldReservations = async () => {
    const next = !autoDeleteOldReservations;
    setAutoDeleteSaving(true);
    try {
      const { data } = await api.put('/backoffice/auto-delete-old-reservations', { enabled: next });
      if (data.success) {
        setAutoDeleteOldReservations(data.auto_delete_old_reservations !== false);
        toast.success(
          data.auto_delete_old_reservations
            ? 'تم تفعيل حذف الحجوزات القديمة تلقائياً'
            : 'تم إيقاف حذف الحجوزات القديمة تلقائياً'
        );
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setAutoDeleteSaving(false);
    }
  };

  const toggleDay = (dayEn) => {
    setWorkingDays((prev) =>
      prev.includes(dayEn) ? prev.filter((d) => d !== dayEn) : [...prev, dayEn]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (workingDays.length === 0) return toast.error('اختر يوم عمل واحد على الأقل');
    if (startHour >= endHour) return toast.error('ساعة البداية يجب أن تكون قبل ساعة النهاية');

    for (const [day, dh] of Object.entries(dayHours)) {
      if (dh.start >= dh.end)
        return toast.error(`ساعة البداية يجب أن تكون قبل ساعة النهاية ليوم ${day}`);
    }
    for (const br of globalBreaks) {
      if (br.start && br.end && br.start >= br.end)
        return toast.error('ساعة بداية الاستراحة يجب أن تكون قبل نهايتها');
    }

    setSaving(true);
    try {
      const payload = {
        working_days: workingDays,
        start_hour: startHour,
        end_hour: endHour,
        vacation_days: existingVacationDays,
        off_hours: perDayOffHours,
        global_breaks: serializeBreaks(globalBreaks),
        day_hours: dayHours,
      };
      const { data } = await api.put('/backoffice/calendar-settings', payload);
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-loading">جاري التحميل...</div>;

  return (
    <div className="settings-page">
      <h1>إعدادات العمل</h1>

      <div className="settings-toggle-card">
        <div className="settings-toggle-info">
          <h2>حذف الحجوزات القديمة تلقائياً</h2>
          <p>
            عند التفعيل، يتم كل يوم الساعة 4:00 صباحاً حذف الحجوزات التي انتهى موعدها
            (حتى أمس فقط — حجوزات اليوم لا تُحذف). لا يؤثر على التعليقات أو التقييمات.
          </p>
        </div>
        <label className="settings-switch">
          <input
            type="checkbox"
            checked={autoDeleteOldReservations}
            onChange={toggleAutoDeleteOldReservations}
            disabled={autoDeleteSaving}
          />
          <span className="settings-slider" />
        </label>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        {/* Working Days */}
        <div className="form-section">
          <h2>أيام العمل</h2>
          <div className="days-grid">
            {ALL_DAYS.map(({ en, ar }) => (
              <button
                key={en}
                type="button"
                className={`day-chip ${workingDays.includes(en) ? 'active' : ''}`}
                onClick={() => toggleDay(en)}
              >
                {ar}
              </button>
            ))}
          </div>
        </div>

        {/* Global Working Hours */}
        <div className="form-section">
          <h2>ساعات العمل الافتراضية</h2>
          <p className="section-desc">تُطبَّق على جميع أيام العمل ما لم يُحدَّد تخصيص لليوم</p>
          <div className="form-row">
            <div className="form-group">
              <label>بداية الدوام</label>
              <input
                type="time"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                dir="ltr"
              />
            </div>
            <div className="form-group">
              <label>نهاية الدوام</label>
              <input
                type="time"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                dir="ltr"
              />
            </div>
          </div>

          {/* Global breaks */}
          <div className="breaks-section">
            <div className="breaks-header">
              <span className="breaks-label">استراحات (اختياري)</span>
              <button
                type="button"
                className="add-break-btn"
                onClick={() =>
                  setGlobalBreaks((prev) => [...prev, { start: '12:00', end: '13:00' }])
                }
              >
                + إضافة استراحة
              </button>
            </div>
            {globalBreaks.map((br, i) => (
              <div key={i} className="break-row">
                <input
                  type="time"
                  value={br.start}
                  onChange={(e) => {
                    const next = [...globalBreaks];
                    next[i] = { ...next[i], start: e.target.value };
                    setGlobalBreaks(next);
                  }}
                  dir="ltr"
                />
                <span className="break-dash">—</span>
                <input
                  type="time"
                  value={br.end}
                  onChange={(e) => {
                    const next = [...globalBreaks];
                    next[i] = { ...next[i], end: e.target.value };
                    setGlobalBreaks(next);
                  }}
                  dir="ltr"
                />
                <button
                  type="button"
                  className="remove-break-btn"
                  onClick={() => setGlobalBreaks((prev) => prev.filter((_, j) => j !== i))}
                  title="حذف"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Per-Day Overrides */}
        {workingDays.length > 0 && (
          <div className="form-section">
            <h2>تخصيص أيام محددة</h2>
            <p className="section-desc">
              اختر يومًا لتعيين ساعات واستراحات مختلفة عن الإعدادات الافتراضية
            </p>
            {ALL_DAYS.filter(({ en }) => workingDays.includes(en)).map(({ en, ar }) => {
              const isCustom = en in dayHours;
              const dh = dayHours[en] || { start: startHour, end: endHour };
              const dayBreaks = parseBreaks(perDayOffHours[en] || []);

              const setDayStart = (val) =>
                setDayHours((prev) => ({
                  ...prev,
                  [en]: { ...(prev[en] || { start: startHour, end: endHour }), start: val },
                }));
              const setDayEnd = (val) =>
                setDayHours((prev) => ({
                  ...prev,
                  [en]: { ...(prev[en] || { start: startHour, end: endHour }), end: val },
                }));
              const setDayBreaksSerialized = (breaks) =>
                setPerDayOffHours((prev) => ({ ...prev, [en]: serializeBreaks(breaks) }));
              const addDayBreak = () =>
                setDayBreaksSerialized([...dayBreaks, { start: '12:00', end: '13:00' }]);
              const removeDayBreak = (idx) =>
                setDayBreaksSerialized(dayBreaks.filter((_, j) => j !== idx));
              const updateDayBreak = (idx, field, val) => {
                const next = [...dayBreaks];
                next[idx] = { ...next[idx], [field]: val };
                setDayBreaksSerialized(next);
              };

              const enableCustom = () =>
                setDayHours((prev) => ({
                  ...prev,
                  [en]: { start: startHour, end: endHour },
                }));
              const disableCustom = () => {
                setDayHours((prev) => {
                  const n = { ...prev };
                  delete n[en];
                  return n;
                });
                setPerDayOffHours((prev) => {
                  const n = { ...prev };
                  delete n[en];
                  return n;
                });
              };

              return (
                <div key={en} className={`day-override-card ${isCustom ? 'custom' : ''}`}>
                  <div className="day-override-header">
                    <span className="day-override-name">{ar}</span>
                    <div className="day-override-toggle">
                      <button
                        type="button"
                        className={`toggle-chip ${!isCustom ? 'active' : ''}`}
                        onClick={disableCustom}
                      >
                        افتراضي
                      </button>
                      <button
                        type="button"
                        className={`toggle-chip ${isCustom ? 'active' : ''}`}
                        onClick={enableCustom}
                      >
                        مخصص
                      </button>
                    </div>
                  </div>

                  {isCustom && (
                    <div className="day-override-body">
                      <div className="form-row">
                        <div className="form-group">
                          <label>بداية الدوام</label>
                          <input
                            type="time"
                            value={dh.start}
                            onChange={(e) => setDayStart(e.target.value)}
                            dir="ltr"
                          />
                        </div>
                        <div className="form-group">
                          <label>نهاية الدوام</label>
                          <input
                            type="time"
                            value={dh.end}
                            onChange={(e) => setDayEnd(e.target.value)}
                            dir="ltr"
                          />
                        </div>
                      </div>
                      <div className="breaks-section">
                        <div className="breaks-header">
                          <span className="breaks-label">استراحات (اختياري)</span>
                          <button type="button" className="add-break-btn" onClick={addDayBreak}>
                            + إضافة استراحة
                          </button>
                        </div>
                        {dayBreaks.map((br, i) => (
                          <div key={i} className="break-row">
                            <input
                              type="time"
                              value={br.start}
                              onChange={(e) => updateDayBreak(i, 'start', e.target.value)}
                              dir="ltr"
                            />
                            <span className="break-dash">—</span>
                            <input
                              type="time"
                              value={br.end}
                              onChange={(e) => updateDayBreak(i, 'end', e.target.value)}
                              dir="ltr"
                            />
                            <button
                              type="button"
                              className="remove-break-btn"
                              onClick={() => removeDayBreak(i)}
                              title="حذف"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button type="submit" className="save-btn" disabled={saving}>
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </button>
      </form>

      <div className="form-card" style={{ marginTop: '1.5rem' }}>
        <ScheduleExceptions />
      </div>
    </div>
  );
}
